/**
 * useMultiBrokerOrder Hook
 * Manages multi-broker order placement with progress tracking.
 * 
 * ⚠️ IMPORTANT: Per ADR-FE-001, this hook only orchestrates order placement.
 * All order operations are handled by backend Order Service.
 */

import { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { placeOrder, setPlacingOrder, setPlacingOrderProgress, clearPlacingOrderProgress } from '../../../store/slices/ordersSlice';
import { useBrokerAccountSelector } from '../../broker-accounts';
import { CreateOrderParams, MultiBrokerOrderResult, Order, extractOrderError, OrderErrorDetails } from '../../../types/order';

export interface UseMultiBrokerOrderResult {
  /**
   * Place orders on multiple broker accounts
   */
  placeMultiBrokerOrder: (params: Omit<CreateOrderParams, 'registration_id' | 'broker_account_id' | 'broker_type'>) => Promise<MultiBrokerOrderResult>;
  /**
   * Place order on a single broker account
   */
  placeSingleOrder: (params: CreateOrderParams) => Promise<Order>;
  /**
   * Order placement in progress
   */
  placingOrder: boolean;
  /**
   * Progress status per broker account
   */
  progress: Record<string, 'pending' | 'success' | 'error'>;
  /**
   * Errors during order placement
   */
  errors: Record<string, string>;
}

export function useMultiBrokerOrder(): UseMultiBrokerOrderResult {
  const dispatch = useAppDispatch();
  const { selectedAccounts } = useBrokerAccountSelector();
  const placingOrder = useAppSelector((state) => state.orders.placingOrder);
  const progress = useAppSelector((state) => state.orders.placingOrderProgress);

  const [errors, setErrors] = useState<Record<string, string>>({});

  /**
   * Place order on a single broker account
   */
  const placeSingleOrder = useCallback(
    async (params: CreateOrderParams): Promise<Order> => {
      const result = await dispatch(placeOrder(params)).unwrap();
      return result;
    },
    [dispatch]
  );

  /**
   * Place orders on multiple broker accounts in parallel
   */
  const placeMultiBrokerOrder = useCallback(
    async (params: Omit<CreateOrderParams, 'registration_id' | 'broker_account_id' | 'broker_type'>): Promise<MultiBrokerOrderResult> => {
      if (selectedAccounts.length === 0) {
        throw new Error('No broker accounts selected');
      }

      // Clear previous progress and errors
      dispatch(clearPlacingOrderProgress());
      setErrors({});
      dispatch(setPlacingOrder(true));

      // Results array for tracking order placement (reserved for future use)
      // const results: Array<{ success: boolean; brokerAccountId: string; brokerName: string; order?: Order; error?: string }> = [];

      // Initialize progress for all selected accounts
      selectedAccounts.forEach((account) => {
        dispatch(
          setPlacingOrderProgress({
            brokerAccountId: account.id,
            status: 'pending',
          })
        );
      });

      // Place orders in parallel
      const orderPromises = selectedAccounts.map(async (account) => {
        try {
          dispatch(
            setPlacingOrderProgress({
              brokerAccountId: account.id,
              status: 'pending',
            })
          );

          const order = await dispatch(
            placeOrder({
              ...params,
              registration_id: account.id, // Use account.id (system UUID) as registration_id
            })
          ).unwrap();

          dispatch(
            setPlacingOrderProgress({
              brokerAccountId: account.id,
              status: 'success',
            })
          );

          return {
            success: true,
            brokerAccountId: account.id,
            brokerName: account.broker_name,
            order,
          };
        } catch (error: unknown) {
          const orderError = extractOrderError(error);
          
          dispatch(
            setPlacingOrderProgress({
              brokerAccountId: account.id,
              status: 'error',
            })
          );

          setErrors((prev) => ({
            ...prev,
            [account.id]: orderError.message,
          }));

          return {
            success: false,
            brokerAccountId: account.id,
            brokerName: account.broker_name,
            error: orderError.message,
            errorDetails: orderError.details,
          };
        }
      });

      const orderResults = await Promise.all(orderPromises);

      dispatch(setPlacingOrder(false));

      // Separate successes and failures
      const successes = orderResults.filter((r) => r.success && r.order).map((r) => r.order!);
      const failures = orderResults
        .filter((r) => !r.success)
        .map((r) => ({
          broker_account_id: r.brokerAccountId,
          broker_name: r.brokerName,
          error: r.error || 'Unknown error',
          errorDetails: (r as any).errorDetails as OrderErrorDetails | undefined,
        }));

      return {
        successes,
        failures,
        totalCount: selectedAccounts.length,
        successCount: successes.length,
        failureCount: failures.length,
      };
    },
    [dispatch, selectedAccounts]
  );

  return {
    placeMultiBrokerOrder,
    placeSingleOrder,
    placingOrder,
    progress,
    errors,
  };
}

